import { AdContentTimePosition, Promo } from '@quickplay/player-interface';
import { Error } from '@quickplay/foundation';
import { VmapConstants } from '../model/VMAPConstants';
import { BrightCoveMetaData } from '../model/BrightCoveMetadata';
import { truncateNumber, createDocumentParsingFailure } from './BrightcovePlayerUtil';

export function getAdCuePointsData(vmapData: string): Promise<BrightCoveMetaData | Error> {
  return new Promise((resolve, reject) => {
    try {
      const xmlDocument = getXmlDocument(vmapData);
      if (!xmlDocument) {
        return reject(createDocumentParsingFailure());
      }
      const brightcoveMetaData = parseVmapData(xmlDocument);
      if (!brightcoveMetaData.contentUrl) {
        return reject(new Error('contentUrl is null or empty'));
      }
      resolve(brightcoveMetaData);
    } catch {
      reject(createDocumentParsingFailure());
    }
  });
}

function parseVmapData(xmlDocument: Document): BrightCoveMetaData {
  // Extract the contentUrl from contenturi Attribite from extensions
  const brightcoveElement = findFirstElementByTagName(xmlDocument, VmapConstants.BC_BRIGHTCOVE);
  const contentUrl = brightcoveElement ? brightcoveElement.getAttribute('contenturi') : null;

  // Extract the thumbnailUrl from thumbnail element from extensions
  const thumbnailElement = findFirstElementByTagName(xmlDocument, VmapConstants.BC_THUMBNAIL_URL);
  const thumbnailUrl = thumbnailElement ? thumbnailElement.textContent : null;

  const adCueMetadata = getPromoMetadata(xmlDocument);
  return { contentUrl, thumbnailUrl, adCueMetadata };
}

function getPromoMetadata(xmlDocument: Document): Promo[] {
  const adCueMetadata: Promo[] = [];
  let adbreakDuration = 0;
  const brightcoveElement = findFirstElementByTagName(xmlDocument, VmapConstants.BC_BRIGHTCOVE);
  const contentLength = Math.ceil(Number(brightcoveElement.getAttribute(VmapConstants.CONTENT_LENGTH)) * 1000);

  const adBreaks = findElementsByTagName(xmlDocument, VmapConstants.AD_BREAK);
  for (let start = 0; start < adBreaks.length; start++) {
    const timeOffset =
      adBreaks[start].getAttribute(VmapConstants.TIMEOFFSET) !== null
        ? adBreaks[start].getAttribute(VmapConstants.TIMEOFFSET)
        : '';
    const ads = findElementsByTagName(adBreaks[start], VmapConstants.AD);

    // iterate through each ad tags available in the adBreaks to create promo object
    for (let i = 0; i < ads.length; i++) {
      const promo: Promo = {
        contentId: '',
        duration: 0,
        start: 0,
        end: 0,
        catalogType: VmapConstants.AD,
        contentDurationMs: contentLength,
      };

      // promo metadata
      promo.contentId = ads[i].getAttribute(VmapConstants.ID) ?? '';
      promo.start = truncateNumber(convertTimeStringToSeconds(xmlDocument, timeOffset) + adbreakDuration, 4);
      promo.duration = truncateNumber(
        convertTimeStringToSeconds(
          xmlDocument,
          findElementsByTagName(adBreaks[start], VmapConstants.DURATION)[i].textContent,
        ),
        4,
      );
      promo.end = truncateNumber(promo.start + promo.duration, 4);
      promo.contentTimePosition = convertContentTimePosition(timeOffset);
      const linearTag: Element | null = adBreaks[start].querySelector(VmapConstants.LINEAR);
      promo.skipOffset = convertTimeStringToSeconds(
        xmlDocument,
        linearTag !== null ? linearTag.getAttribute(VmapConstants.SKIP_OFFSET) : null,
      );
      promo.skippable = promo.skipOffset ? true : false;

      // Update adbreakDuration with duration of current ad to set the start of the next ad.
      adbreakDuration =
        adbreakDuration +
        convertTimeStringToSeconds(
          xmlDocument,
          findElementsByTagName(adBreaks[start], VmapConstants.DURATION)[i].textContent,
        );
      adCueMetadata.push(promo);
    }
  }

  return adCueMetadata;
}

function getXmlDocument(vmapData: string): Document | undefined {
  if (!window['DOMParser']) {
    return undefined;
  }

  const domParser = new DOMParser();
  return domParser.parseFromString(vmapData, VmapConstants.TEXT_XML);
}

/**
 * converts timestring in the XML document to seconds.
 * @param xmlDocument Vmap XML document
 * @param timeString string
 * @returns number
 */
function convertTimeStringToSeconds(xmlDocument: any, timeString: string | null): number {
  if (timeString === null || timeString === VmapConstants.START || timeString === '0') {
    return 0;
  }

  if (timeString === VmapConstants.END) {
    const brightcoveElement = findFirstElementByTagName(xmlDocument, VmapConstants.BC_BRIGHTCOVE);
    return Number(brightcoveElement.getAttribute(VmapConstants.CONTENT_LENGTH));
  }

  const array = timeString.split(':');
  return parseFloat(array[0]) * 60 * 60 + parseFloat(array[1]) * 60 + parseFloat(array[2]);
}

function convertContentTimePosition(timeString: string | null): AdContentTimePosition {
  if (timeString === null || timeString === VmapConstants.START || timeString === '0') {
    return AdContentTimePosition.PREROLL;
  }

  if (timeString === VmapConstants.END) {
    return AdContentTimePosition.POSTROLL;
  }

  return AdContentTimePosition.MIDROLL;
}

function findElementsByTagName(element: any, tagName: string): any[] {
  let result: any[] = [];

  if (element.nodeType === 1 && element.tagName.toLowerCase() === tagName.toLowerCase()) {
    result.push(element);
  }

  for (let i = 0; i < element.childNodes.length; i++) {
    result = result.concat(findElementsByTagName(element.childNodes[i], tagName));
  }

  return result;
}

function findFirstElementByTagName(element: any, tagName: string): any | null {
  if (element.nodeType === 1 && element.tagName.toLowerCase() === tagName.toLowerCase()) {
    return element;
  }

  for (let i = 0; i < element.childNodes.length; i++) {
    const found = findFirstElementByTagName(element.childNodes[i], tagName);

    if (found) {
      return found;
    }
  }

  return null;
}
